import { Link } from 'react-router-dom';
import {
  Activity,
  Bot,
  Brain,
  Code2,
  Database,
  FileText,
  GitBranch,
  KeyRound,
  Layers,
  Package,
  Plug,
  Rocket,
  Search,
  Settings,
  ShieldCheck,
  Terminal,
  Wrench,
} from 'lucide-react';

const SYSTEM_GROUPS = [
  {
    title: '构建与配置',
    description: '管理 builder、业务包、schema、DSL、模板与配置发布链。',
    icon: Wrench,
    tone: 'bg-blue-50 text-blue-800 border-blue-100',
    links: [
      { label: 'Builder', to: '/admin/builder', icon: Wrench },
      { label: 'Business Packages', to: '/admin/packs', icon: Package },
      { label: 'SDK Data', to: '/admin/sdk-data', icon: Database },
      { label: 'Schema Editor', to: '/admin/schema', icon: Database },
      { label: 'DSL Designer', to: '/admin/dsl-designer', icon: Layers },
      { label: 'View Templates', to: '/admin/view-templates', icon: FileText },
      { label: 'Document Templates', to: '/admin/templates', icon: FileText },
      { label: 'Config Compiler', to: '/admin/config-compiler', icon: Code2 },
      { label: 'Config Versions', to: '/admin/config-versions', icon: Layers },
      { label: 'Domain Configs', to: '/admin/domain-configs', icon: Settings },
      { label: 'Menu Config', to: '/admin/menu-config', icon: Settings },
    ],
  },
  {
    title: '工作流与自动化',
    description: '维护工作流、脚本、周期任务与内部自动化控制面。',
    icon: GitBranch,
    tone: 'bg-emerald-50 text-emerald-800 border-emerald-100',
    links: [
      { label: 'Workflows', to: '/admin/workflows', icon: GitBranch },
      { label: 'Workflow Templates', to: '/admin/workflow-templates', icon: GitBranch },
      { label: 'Scripts', to: '/admin/scripts', icon: Terminal },
      { label: 'Recurring Tasks', to: '/admin/recurring-tasks', icon: Activity },
      { label: 'Task Center', to: '/admin/tasks', icon: ShieldCheck },
      { label: 'Executive Nudges', to: '/admin/executive-nudges', icon: Bot },
    ],
  },
  {
    title: 'AI Runtime',
    description: '维护 AI command、agent、skills、prompts、memory 与 brain runtime。',
    icon: Brain,
    tone: 'bg-violet-50 text-violet-800 border-violet-100',
    links: [
      { label: 'AI Command Center', to: '/admin/command-center', icon: Bot },
      { label: 'AI Team', to: '/admin/team', icon: Bot },
      { label: 'Agent Lab', to: '/admin/agentlab', icon: Bot },
      { label: 'Skills', to: '/admin/skills', icon: Plug },
      { label: 'Skill Config', to: '/admin/skill-config', icon: Settings },
      { label: 'Prompts', to: '/admin/prompts', icon: FileText },
      { label: 'Training Data', to: '/admin/training', icon: Database },
      { label: 'Memory', to: '/admin/memory', icon: Brain },
      { label: 'Tools', to: '/admin/tools', icon: Wrench },
      { label: 'AI Control Plane', to: '/admin/ai-control-plane', icon: KeyRound },
      { label: 'Brain Logs', to: '/admin/brain-logs', icon: Search },
      { label: 'Brain Soul', to: '/admin/brain-soul', icon: Brain },
      { label: 'Brain Schedule', to: '/admin/brain-schedule', icon: Activity },
    ],
  },
  {
    title: '系统、部署与监控',
    description: '处理 bridges、data transfer、deploy、print、analytics、logs 与 access evidence。',
    icon: Rocket,
    tone: 'bg-slate-50 text-slate-800 border-slate-200',
    links: [
      { label: 'API Center', to: '/admin/api-center', icon: KeyRound },
      { label: 'Bridges', to: '/admin/bridges', icon: Plug },
      { label: 'Legacy Connectors', to: '/admin/legacy-connectors', icon: Database },
      { label: 'Data Transfer', to: '/admin/data-transfer', icon: Database },
      { label: 'Deploy Control', to: '/admin/deploy-control', icon: Rocket },
      { label: 'Print Center', to: '/admin/print-center', icon: FileText },
      { label: 'Platform Analytics', to: '/admin/analytics', icon: Activity },
      { label: 'Log Explorer', to: '/admin/logs', icon: Search },
      { label: 'Access Log', to: '/admin/access-log', icon: Activity },
      { label: 'User Performance', to: '/admin/user-performance', icon: ShieldCheck },
      { label: 'Job Timeline', to: '/admin/job-timeline', icon: GitBranch },
    ],
  },
];

export default function PlatformSystemsPage() {
  return (
    <div className="mx-auto max-w-7xl space-y-8 px-4 py-6">
      <header className="rounded-3xl border border-slate-200 bg-slate-950 p-6 text-white shadow-sm">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.22em] text-slate-200">
          <Settings className="h-3.5 w-3.5" />
          Platform Systems
        </div>
        <h1 className="mt-4 text-3xl font-semibold tracking-tight">平台系统控制台</h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">
          这里是高级平台工具的落地页。日常经营先走 Command、Revenue Ops、Delivery Ops、CEO；只有需要改 builder、AI runtime、配置、部署或日志时才进入这里。
        </p>
      </header>

      <section className="grid gap-4 md:grid-cols-2">
        {SYSTEM_GROUPS.map((group) => {
          const Icon = group.icon;
          return (
            <article key={group.title} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className={`inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm font-medium ${group.tone}`}>
                <Icon className="h-4 w-4" />
                {group.title}
              </div>
              <p className="mt-3 text-sm leading-6 text-slate-600">{group.description}</p>
              <div className="mt-5 grid gap-2 sm:grid-cols-2">
                {group.links.map((link) => {
                  const LinkIcon = link.icon;
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                    >
                      <LinkIcon className="h-4 w-4 text-slate-500" />
                      <span className="truncate">{link.label}</span>
                    </Link>
                  );
                })}
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
}
